import { Box, Button, Divider, Drawer, IconButton, Typography } from '@mui/material'
import { IconX } from '@tabler/icons-react'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { CheckboxMultiple } from '~/components/Form/CheckboxMultiple'
import { Input } from '~/components/Form/Input'
import { SearchQueysData, useQueryString } from '~/hooks'
import { byInvoiceOptions, contractInvoiceOptions, invoicePaymentTypeOptions } from '~/utils/options'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { EInvoiceStatus } from '~/graphql/types'

type FilterData = {
  by?: string[],
  status?: string[],
  begin?: string,
  end?: string
  dueBegin?: string,
  dueEnd?: string
}

type InvoiceListDrawerProps = {
  drawerIsOpen: boolean
  setDrawerIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  toggleDrawer: () => void
  queryKeys: SearchQueysData[]
}

const schema = yup
  .object({
    begin: yup.string().test('isValid', (value, validation) => {
      if(value && Boolean(!validation.parent.end)) {
        return validation.createError({
          message: 'Este campo é obrigatório.',
          path: 'end'
        })
      }
      return true
    }),
    end: yup.string()
      .test('isValid', (value, validation) => {
        if(value && Boolean(!validation.parent.begin)) {
          return validation.createError({
            message: 'Este campo é obrigatório.',
            path: 'begin'
          })
        }
        return true
      }),
    dueBegin: yup.string().test('isValid', (value, validation) => {
      if(value && Boolean(!validation.parent.dueEnd)) {
        return validation.createError({
          message: 'Este campo é obrigatório.',
          path: 'dueEnd'
        })
      }
      return true
    }),
    dueEnd: yup.string()
      .test('isValid', (value, validation) => {
        if(value && Boolean(!validation.parent.dueBegin)) {
          return validation.createError({
            message: 'Este campo é obrigatório.',
            path: 'dueBegin'
          })
        }
        return true
      }),
  })
  .required()

export const InvoiceListDrawer: React.FC<InvoiceListDrawerProps> = ({ drawerIsOpen, toggleDrawer, setDrawerIsOpen, queryKeys }) => {
  const formMethods = useForm({
    resolver: yupResolver(schema)
  })

  const { searchQuerys, setSearchParams } = useQueryString()
  const queryValues = searchQuerys(queryKeys)

  useEffect(() => {
    if(drawerIsOpen) {
      formMethods.setValue('begin', queryValues.begin)
      formMethods.setValue('end', queryValues.end)
      formMethods.setValue('duebegin', queryValues.duebegin)
      formMethods.setValue('dueEnd', queryValues.dueEnd)
      formMethods.setValue('status', queryValues.status)
      formMethods.setValue('by', queryValues.by)
      formMethods.setValue('paymentTypes', queryValues.paymentTypes)
    }
  }, [drawerIsOpen])

  const handleFilters = (filters: FilterData & { paymentTypes?: string[] }) => {
    setDrawerIsOpen(false)
    setSearchParams(state => {
      state.set('page', '0')
      if(filters?.status && filters?.status?.length > 0 || queryValues?.status?.length > 0) {
        state.set('status', JSON.stringify(filters.status) || queryValues.status)
      } else {
        state.delete('status')
      }
      if(filters?.by && filters?.by?.length > 0) {
        state.set('by', JSON.stringify(filters.by))
      } else {
        state.delete('by')
      }
      const hasPaidStatus = (filters?.status || queryValues.status || [])?.includes(EInvoiceStatus.paid as unknown as string)
      if (hasPaidStatus && (filters?.paymentTypes?.length || queryValues?.paymentTypes?.length)) {
        state.set('paymentTypes', JSON.stringify(filters?.paymentTypes || queryValues.paymentTypes))
      } else {
        state.delete('paymentTypes')
      }
      filters.dueBegin  || queryValues.dueBegin  ? state.set('dueBegin', filters.dueBegin || queryValues.dueBegin) : state.delete('dueBegin')
      filters.dueEnd  || queryValues.dueEnd  ? state.set('dueEnd', filters.dueEnd || queryValues.dueEnd) : state.delete('dueEnd')
      filters.begin  || queryValues.begin  ? state.set('begin', filters.begin || queryValues.begin) : state.delete('begin')
      filters.end  || queryValues.end  ? state.set('end', filters.end || queryValues.end) : state.delete('end')
      return state
    })
  }

  const onRemoveFilters = () => {
    setDrawerIsOpen(false)
    setSearchParams(state => {
      state.set('page', '0')
      state.delete('by')
      state.delete('status')
      state.delete('dueBegin')
      state.delete('dueEnd')
      state.delete('begin')
      state.delete('end')
      state.delete('paymentTypes')
      return state
    })
  }

  return (
    <Drawer PaperProps={{ sx: { maxWidth: '560px', width: '100%', padding: '2rem' } }} anchor='right' open={drawerIsOpen} onClose={toggleDrawer}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography fontWeight={500} variant='h5'>Filtrar por</Typography>
        <IconButton type='button' onClick={() => toggleDrawer()}>
          <IconX  />
        </IconButton>
      </Box>

      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit((formData) => handleFilters(formData))}>
          <Typography sx={{ paddingTop: '1rem' }} fontWeight={500}>Data de criação</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 0 2rem 0' }}>
            <Input name='begin' type='date' />
            <Typography>Até</Typography>
            <Input name='end' type='date' />
          </Box>

          <Divider />

          <Typography sx={{ paddingTop: '1rem' }} fontWeight={500}>Data de vencimento</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 0 2rem 0' }}>
            <Input name='dueBegin' type='date' />
            <Typography>Até</Typography>
            <Input name='dueEnd' type='date' />
          </Box>

          <Divider sx={{ marginBottom: '1.4rem' }} />

          <Typography fontWeight={500}>Status</Typography>
          <CheckboxMultiple name='status' options={contractInvoiceOptions.filter(item => item.value !== EInvoiceStatus.requested)} />
          {(formMethods.watch('status') || [])?.includes(EInvoiceStatus.paid as unknown as string) && (
            <Box sx={{ paddingTop: '1rem' }}>
              <Typography sx={{ paddingTop: '0' }} fontWeight={500}>Tipos de pagamento</Typography>
              <CheckboxMultiple name='paymentTypes' options={invoicePaymentTypeOptions} />
            </Box>
          )}
          
          <Divider sx={{ margin: '1.4rem 0' }} />

          <Typography fontWeight={500}>Via</Typography>
          <CheckboxMultiple name='by' options={byInvoiceOptions} />

          <Divider sx={{ marginTop: '1.4rem' }} />

          <Box sx={{ display: 'flex', padding: '2rem 0', gap: '1rem', justifyContent: 'flex-end' }}>
            <Button onClick={() => onRemoveFilters()} type='button' fullWidth color='secondary'>Limpar</Button>
            <Button type='submit' fullWidth color='primary'>Aplicar</Button>
          </Box>
        </form>
      </FormProvider>
    </Drawer>
  )
}
